import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Chip,
} from '@mui/material';
import { Search, Edit } from '@mui/icons-material';
import { employeesAPI, salaryDetailsAPI } from '../services/api';

const PayslipDetails = () => {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [salaryFormData, setSalaryFormData] = useState({
    basic: '',
    daily_allowance: '',
    house_rent_allowance: '',
    telephone_allowance: '',
    medical_allowance: '',
    food_allowance: '',
    others_allowance: '',
    overtime_rate: '',
    loyalty_bonus: '',
    professional_tax: '',
    tds: '',
    epf: '',
    esi: '',
    ctc_month: '',
    ctc_year: '',
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEmployees = async () => {
    try {
      const response = await employeesAPI.getAll({ search, limit: 100 });
      if (response.data.success) {
        setEmployees(response.data.data.employees);
      }
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleEdit = async (employee) => {
    setSelectedEmployee(employee);
    try {
      const response = await salaryDetailsAPI.getByEmployee(employee.id);
      if (response.data.success) {
        setSalaryFormData({
          basic: response.data.data.basic || '',
          daily_allowance: response.data.data.daily_allowance || '',
          house_rent_allowance: response.data.data.house_rent_allowance || '',
          telephone_allowance: response.data.data.telephone_allowance || '',
          medical_allowance: response.data.data.medical_allowance || '',
          food_allowance: response.data.data.food_allowance || '',
          others_allowance: response.data.data.others_allowance || '',
          overtime_rate: response.data.data.overtime_rate || '',
          loyalty_bonus: response.data.data.loyalty_bonus || '',
          professional_tax: response.data.data.professional_tax || '',
          tds: response.data.data.tds || '',
          epf: response.data.data.epf || '',
          esi: response.data.data.esi || '',
          ctc_month: response.data.data.ctc_month || '',
          ctc_year: response.data.data.ctc_year || '',
        });
      } else {
        // No salary details, set empty
        setSalaryFormData({
          basic: '',
          daily_allowance: '',
          house_rent_allowance: '',
          telephone_allowance: '',
          medical_allowance: '',
          food_allowance: '',
          others_allowance: '',
          overtime_rate: '',
          loyalty_bonus: '',
          professional_tax: '',
          tds: '',
          epf: '',
          esi: '',
          ctc_month: '',
          ctc_year: '',
        });
      }
    } catch (error) {
      // No salary details
      setSalaryFormData({
        basic: '',
        daily_allowance: '',
        house_rent_allowance: '',
        telephone_allowance: '',
        medical_allowance: '',
        food_allowance: '',
        others_allowance: '',
        overtime_rate: '',
        loyalty_bonus: '',
        professional_tax: '',
        tds: '',
        epf: '',
        esi: '',
        ctc_month: '',
        ctc_year: '',
      });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      await salaryDetailsAPI.update(selectedEmployee.id, salaryFormData);
      setMessage({ type: 'success', text: 'Salary details saved successfully' });
      setShowDialog(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to save salary details' });
    }
  };

  const calculateCTC = () => {
    const incomeFields = ['basic', 'daily_allowance', 'house_rent_allowance', 'telephone_allowance', 'medical_allowance', 'food_allowance', 'others_allowance', 'loyalty_bonus'];
    let total = 0;
    incomeFields.forEach(field => {
      const value = parseFloat(salaryFormData[field]) || 0;
      total += value;
    });
    const ctcMonth = total;
    const ctcYear = total * 12;
    setSalaryFormData(prev => ({ ...prev, ctc_month: ctcMonth.toFixed(2), ctc_year: ctcYear.toFixed(2) }));
  };

  useEffect(() => {
    if (showDialog) {
      calculateCTC();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialog, salaryFormData.basic, salaryFormData.daily_allowance, salaryFormData.house_rent_allowance, salaryFormData.telephone_allowance, salaryFormData.medical_allowance, salaryFormData.food_allowance, salaryFormData.others_allowance, salaryFormData.loyalty_bonus]);

  return (
    <Box sx={{ bgcolor: '#0A1929', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#D4AF37' }}>
            Payslip Details Management
          </Typography>
        </Box>

        {message.text && (
          <Alert
            severity={message.type}
            onClose={() => setMessage({ type: '', text: '' })}
            sx={{
              mb: 3,
              bgcolor: '#1A2E14',
              color: message.type === 'success' ? '#81C784' : '#EF9A9A',
              border: `1px solid ${message.type === 'success' ? '#81C784' : '#EF9A9A'}`,
              '& .MuiAlert-icon': { color: message.type === 'success' ? '#81C784' : '#EF9A9A' }
            }}
          >
            {message.text}
          </Alert>
        )}

        <TextField
          fullWidth
          placeholder="Search employees..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#D4AF37' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#1A2E14',
              color: '#ffffff',
              '& fieldset': { borderColor: '#D4AF37' },
              '&:hover fieldset': { borderColor: '#C19B2E' },
            },
          }}
        />

        <Card sx={{ bgcolor: '#1A2E14', border: '1px solid #D4AF37', borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} sx={{ maxHeight: 600, bgcolor: 'transparent', boxShadow: 'none' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#2A3E24' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Basic Salary</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((emp) => (
                    <TableRow key={emp.id} hover sx={{ '&:hover': { bgcolor: 'rgba(212,175,55,0.05)' } }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF' }}>
                          {emp.first_name} {emp.last_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                          {emp.employee_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                          {emp.department || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                          {/* Would need to fetch salary details here, but for simplicity, show if exists */}
                          -
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={emp.is_active ? 'Active' : 'Inactive'}
                          sx={{
                            bgcolor: emp.is_active ? 'rgba(129, 199, 132, 0.2)' : 'rgba(239, 154, 154, 0.2)',
                            color: emp.is_active ? '#81C784' : '#EF9A9A',
                            border: `1px solid ${emp.is_active ? '#81C784' : '#EF9A9A'}`,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(emp)}
                          sx={{ color: '#D4AF37', '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Edit Salary Details Dialog */}
        <Dialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600 }}>
            {selectedEmployee ? `Edit Salary Details - ${selectedEmployee.first_name} ${selectedEmployee.last_name}` : 'Edit Salary Details'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6">Income Components</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Basic Salary" type="number" value={salaryFormData.basic} onChange={(e) => setSalaryFormData({...salaryFormData, basic: e.target.value})} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Daily Allowance" type="number" value={salaryFormData.daily_allowance} onChange={(e) => setSalaryFormData({...salaryFormData, daily_allowance: e.target.value})} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="House Rent Allowance" type="number" value={salaryFormData.house_rent_allowance} onChange={(e) => setSalaryFormData({...salaryFormData, house_rent_allowance: e.target.value})} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Telephone Allowance" type="number" value={salaryFormData.telephone_allowance} onChange={(e) => setSalaryFormData({...salaryFormData, telephone_allowance: e.target.value})} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Medical Allowance" type="number" value={salaryFormData.medical_allowance} onChange={(e) => setSalaryFormData({...salaryFormData, medical_allowance: e.target.value})} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Food Allowance" type="number" value={salaryFormData.food_allowance} onChange={(e) => setSalaryFormData({...salaryFormData, food_allowance: e.target.value})} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Other Allowance" type="number" value={salaryFormData.others_allowance} onChange={(e) => setSalaryFormData({...salaryFormData, others_allowance: e.target.value})} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Overtime Rate" type="number" value={salaryFormData.overtime_rate} onChange={(e) => setSalaryFormData({...salaryFormData, overtime_rate: e.target.value})} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Loyalty Bonus" type="number" value={salaryFormData.loyalty_bonus} onChange={(e) => setSalaryFormData({...salaryFormData, loyalty_bonus: e.target.value})} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6">Deductions</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Professional Tax" type="number" value={salaryFormData.professional_tax} onChange={(e) => setSalaryFormData({...salaryFormData, professional_tax: e.target.value})} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="TDS" type="number" value={salaryFormData.tds} onChange={(e) => setSalaryFormData({...salaryFormData, tds: e.target.value})} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="EPF" type="number" value={salaryFormData.epf} onChange={(e) => setSalaryFormData({...salaryFormData, epf: e.target.value})} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="ESI" type="number" value={salaryFormData.esi} onChange={(e) => setSalaryFormData({...salaryFormData, esi: e.target.value})} />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6">CTC Information (Calculated)</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="CTC/Month" type="number" value={salaryFormData.ctc_month} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="CTC/Year" type="number" value={salaryFormData.ctc_year} InputProps={{ readOnly: true }} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#D4AF37', color: '#1A2E14' }}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default PayslipDetails;